import React, { useEffect, useState } from 'react';
import { Stethoscope, BarChart, Check, AlertCircle, XCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getFileURL } from '../../utils/fileURL';
import { useMasterLabel } from '../../hooks/useMasterLabel';

// Per-row translated profession name. Profession objects come from the doctor doc and
// may be either an object {professionId, professionName} or a bare ID string.
const ProfessionName = ({ profession }) => {
    const id = typeof profession === 'object' ? (profession?.professionId || profession?._id) : profession;
    const fallback = typeof profession === 'object' ? (profession?.professionName || '') : (profession || '');
    const label = useMasterLabel('masterProfession', id, 'professionName', fallback);
    return <>{label}</>;
};

const WelcomeSection = ({ booking, loading }) => {
  const { t } = useTranslation('dashboard');
  const [currentTime, setCurrentTime] = useState(new Date());
  const { loading: doctorLoading, selectedDoctor } = useSelector((state) => state.doctor);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const formattedDate = currentTime.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const formattedTime = currentTime.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const cards = [
    { id: 'booked', label: t('dashboard.analytics_total'), icon: BarChart, bg: 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B]' },
    { id: 'completed', label: t('dashboard.analytics_completed'), icon: Check, bg: 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B]' },
    { id: 'missedout', label: t('dashboard.analytics_missed_out'), icon: AlertCircle, bg: 'bg-gradient-to-r from-[#6B7280] to-[#4B5563]' },
    { id: 'canceled', label: t('dashboard.analytics_canceled'), icon: XCircle, bg: 'bg-gradient-to-r from-[#9CA3AF] to-[#6B7280]' },
  ];

  const userRole = localStorage.getItem('Role');

  // Calculate age from DOB
  const calculateAge = (dob) => {
    if (!dob) return null;
    const today = new Date();
    const birth = new Date(dob);
    // Use UTC getters for birthDate to avoid timezone shift on DOB
    let age = today.getFullYear() - birth.getUTCFullYear();
    const m = today.getMonth() - birth.getUTCMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getUTCDate())) age--;
    return age >= 0 ? age : null;
  };

  // Use API data if available, otherwise use default values
  const getBookingData = () => {
    if (booking?.counts) {
      return {
        booked: { count: booking.total || 0, trend: 'up' },
        completed: { count: booking.counts.Completed || 0, trend: 'up' },
        missedout: { count: booking.counts.NoShow || 0, trend: 'up' },
        canceled: { count: booking.counts.Canceled || 0, trend: 'down' },
      };
    }
    return {
      booked: { count: 0, trend: 'up' },
      completed: { count: 0, trend: 'up' },
      missedout: { count: 0, trend: 'up' },
      canceled: { count: 0, trend: 'down' },
    };
  };

  const currentBookingData = getBookingData();

  return (
    <div
      data-testid="welcome-section"
      className="grid grid-cols-1 gap-4 w-full xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]"
    >
      {/* Left Section: Doctor Info and Welcome */}
      <motion.div
        data-testid="welcome-left-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200/50 shadow-md backdrop-blur-sm flex items-center justify-center"
      >
        {doctorLoading && !selectedDoctor ? (
          <div data-testid="welcome-skeleton" className="space-y-4 w-full max-w-md">
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-full bg-gray-200 animate-pulse"></div>
              <div className="text-center sm:text-left flex-1 space-y-2">
                <div className="h-6 bg-gray-200 rounded animate-pulse w-32 mx-auto sm:mx-0"></div>
                <div className="h-5 bg-gray-200 rounded animate-pulse w-40 mx-auto sm:mx-0"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse w-24 mx-auto sm:mx-0"></div>
              </div>
            </div>
          </div>
        ) : (
          <div data-testid="welcome-content" className="space-y-4 w-full max-w-md">
            <div
              data-testid="doctor-info-section"
              className="flex flex-col sm:flex-row gap-4 items-center"
            >
              <motion.div
                data-testid="doctor-avatar-container"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className="relative"
              >
                <div
                  data-testid="doctor-avatar"
                  className="relative w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-full border-3 border-[#4ABDBD] shadow-lg"
                >
                  {selectedDoctor?.image ? (
                    <img
                      data-testid="doctor-image"
                      src={getFileURL(selectedDoctor.image)}
                      alt={`${t('dashboard.dr_prefix')} ${selectedDoctor?.name}`}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <div
                      data-testid="doctor-initial"
                      className="w-full h-full rounded-full bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] flex items-center justify-center"
                    >
                      <span className="text-white font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
                        {selectedDoctor?.name?.charAt(0).toUpperCase() || 'D'}
                      </span>
                    </div>
                  )}
                  <div
                    data-testid="stethoscope-badge"
                    className="absolute -bottom-1 -right-1 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-full p-1.5 sm:p-2 border-2 border-white"
                  >
                    <Stethoscope
                      data-testid="stethoscope-icon"
                      className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-white"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </motion.div>
              <motion.div
                data-testid="doctor-details"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="text-center sm:text-left flex-1"
              >
                <p
                  data-testid="welcome-message"
                  className="text-sm sm:text-lg md:text-2xl font-bold text-[#4ABDBD] mb-1 drop-shadow-sm"
                >
                  {userRole === 'doctor' ? t('dashboard.welcome_back') : t('dashboard.doctor_overview', 'Doctor Overview')}
                </p>
                <h2
                  data-testid="doctor-name"
                  className="text-xs sm:text-base md:text-lg font-bold text-gray-900 mb-1"
                >
                  {t('dashboard.dr_prefix')} {selectedDoctor?.name} {selectedDoctor?.surname || ''}
                </h2>
                <div data-testid="doctor-attributes" className="flex flex-wrap gap-2 mb-1">
                  {selectedDoctor?.gender && (
                    <span data-testid="doctor-gender-chip" className="text-gray-600 text-xs font-medium border border-gray-200 bg-gray-50 px-2 py-1 rounded-full capitalize">
                      {t(`common.gender.${selectedDoctor.gender.toLowerCase()}`, selectedDoctor.gender)}
                    </span>
                  )}
                  {calculateAge(selectedDoctor?.dob) !== null && (
                    <span data-testid="doctor-age-chip" className="text-gray-600 text-xs font-medium border border-gray-200 bg-gray-50 px-2 py-1 rounded-full">
                      {calculateAge(selectedDoctor.dob)} {t('dashboard.years', 'yrs')}
                    </span>
                  )}
                </div>
                <div data-testid="doctor-profession-row" className="flex flex-wrap gap-1 items-center">
                  {Array.isArray(selectedDoctor?.profession) && selectedDoctor.profession.length > 0 ? (
                    <>
                      <span data-testid="doctor-profession-primary-chip" className="text-[#4ABDBD] text-xs w-fit font-medium border border-[#4ABDBD]/20 bg-[#4ABDBD]/10 px-2 py-1 rounded-full">
                        <ProfessionName profession={selectedDoctor.profession[0]} />
                      </span>
                      {selectedDoctor.profession.length > 1 && (
                        <div data-testid="doctor-profession-more-wrap" className="relative group">
                          <span data-testid="doctor-profession-more-chip" className="text-[#4ABDBD] text-xs w-fit font-medium border border-[#4ABDBD]/20 bg-[#4ABDBD]/10 px-2 py-1 rounded-full cursor-default">
                            +{selectedDoctor.profession.length - 1}
                          </span>
                          <div data-testid="doctor-profession-tooltip" className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                            <div className="bg-gray-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap flex flex-col gap-1">
                              {selectedDoctor.profession.slice(1).map((p, i) => (
                                <span key={i} data-testid={`doctor-profession-tooltip-item-${i}`}><ProfessionName profession={p} /></span>
                              ))}
                              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : selectedDoctor?.profession?.professionName ? (
                    <span data-testid="doctor-profession-single-chip" className="text-[#4ABDBD] text-xs w-fit font-medium border border-[#4ABDBD]/20 bg-[#4ABDBD]/10 px-2 py-1 rounded-full">
                      <ProfessionName profession={selectedDoctor.profession} />
                    </span>
                  ) : (
                    <span data-testid="doctor-profession-default-chip" className="text-[#4ABDBD] text-xs w-fit font-medium border border-[#4ABDBD]/20 bg-[#4ABDBD]/10 px-2 py-1 rounded-full">
                      {t('dashboard.general_doctor')}
                    </span>
                  )}
                </div>

              </motion.div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Right Section: Booking Analytics */}
      <motion.div
        data-testid="doctor-booking-analytics"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200/50 shadow-md backdrop-blur-sm relative"
      >
        <div data-testid="doctor-booking-analytics-header" className="flex items-center justify-between mb-4">
          <h3 data-testid="doctor-booking-analytics-title" className="text-xs sm:text-sm md:text-base font-bold text-gray-900 flex items-center">
            <BarChart
              data-testid="doctor-booking-analytics-icon"
              className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 mr-2 text-[#4ABDBD]"
              aria-hidden="true"
            />
            {t('dashboard.booking_analytics')}
          </h3>
        </div>
        <AnimatePresence>
          <motion.div
            data-testid="doctor-booking-analytics-cards"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4"
          >
            {cards.map((card) => (
              <motion.div
                key={card.id}
                data-testid={`doctor-booking-analytics-card-${card.id}`}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="bg-gradient-to-br from-white to-gray-50 rounded-lg p-2.5 sm:p-3 md:p-4 border border-gray-200/50 shadow-sm hover:shadow-md transition-all duration-300 w-full"
                aria-label={`${card.label}: ${currentBookingData[card.id]?.count.toLocaleString()} ${t('dashboard.appointments', 'appointments')}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1.5 sm:space-x-2 flex-1 min-w-0">
                    <div
                      data-testid={`doctor-booking-analytics-card-${card.id}-icon-wrap`}
                      className={`p-1.5 sm:p-2 md:p-3 rounded-[20px] shadow flex-shrink-0 ${card.bg}`}
                    >
                      <card.icon
                        data-testid={`doctor-booking-analytics-card-${card.id}-icon`}
                        className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-white"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p data-testid={`doctor-booking-analytics-card-${card.id}-value`} className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-gray-900 truncate">
                        {loading ? (
                          <span data-testid={`doctor-booking-analytics-card-${card.id}-skeleton`} className="inline-flex items-center">
                            <span className="w-8 sm:w-10 md:w-12 h-4 sm:h-5 bg-gray-200 rounded animate-pulse"></span>
                          </span>
                        ) : (
                          currentBookingData[card.id]?.count.toLocaleString()
                        )}
                      </p>
                      <p data-testid={`doctor-booking-analytics-card-${card.id}-label`} className="text-[0.6rem] sm:text-[0.65rem] md:text-xs lg:text-sm text-gray-600 font-medium truncate">
                        {card.label}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default WelcomeSection;